// 마을 아래 가장자리 얇은 띠 하나(플레이 영역·조이스틱·버튼 밖). 두 가지만 보인다:
//  - 큰 새 버전(1MB 이상): "새 업데이트 12.4 MB — 다음 접속 때 적용됩니다 · [지금 적용]". 작은 업데이트는 띠 없이 다음 접속에 저절로 적용된다(2026-10-27 사용자 요청).
//  - 첫 접속 데이터 받기(처음에 전부 한 번에): "게임 데이터 받는 중 63% · 26 / 42 MB" 글자만(버튼 없음), 받는 동안에만.
// 전투 중에는 절대 나타나지 않고 새로고침을 강요하지 않는다. 닫으면 이번 접속에서는 업데이트 안내가 다시 나오지 않는다.
import { C, FONT } from '../ui/dom';
import { formatBytes } from './patch';
import { applyUpdateNow, onPwaChange, pwaState } from './register';
import { allDataStatus } from './regionData';
import type { BundleStatus } from './prefetch';

export interface BandOpts {
  /** 마을 장면이고 다른 창(가방·스테이지 지도·대화)이 열려 있지 않은가 */
  visible: () => boolean;
}

/** 이보다 작은 업데이트는 띠를 띄우지 않는다(다음 접속에 조용히 적용) */
export const UPDATE_BAND_MIN_BYTES = 1024 * 1024;

/** 업데이트 띠를 띄울 크기인가(순수). 크기를 모르면(0) 띄우지 않는다 */
export function bigUpdate(bytes: number): boolean { return bytes >= UPDATE_BAND_MIN_BYTES; }

/** 데이터 받기 문구(순수): 받는 중일 때만, 아니면 '' */
export function dataText(st: BundleStatus): string {
  if (st.state !== 'running' || st.bytesTotal <= 0) return '';
  const mb = (b: number): string => (b / (1024 * 1024)).toFixed(b >= 10 * 1024 * 1024 ? 0 : 1);
  return '게임 데이터 받는 중 ' + st.pct + '% · ' + mb(st.bytesDone) + ' / ' + mb(st.bytesTotal) + ' MB';
}

/** 띠 문구(순수) */
export function bandText(bytes: number): string {
  return '새 업데이트 ' + (bytes > 0 ? formatBytes(bytes) + ' ' : '') + '— 다음 접속 때 적용됩니다';
}

export function startPwaBand(root: HTMLElement, opts: BandOpts): { update(): void } {
  const band = document.createElement('div');
  band.dataset.testid = 'pwa-band';
  band.style.cssText = 'position:absolute;left:50%;bottom:max(6px,var(--dh-sb,0px));transform:translateX(-50%);display:none;align-items:center;gap:8px;max-width:calc(100% - 24px);box-sizing:border-box;padding:2px 4px 2px 12px;' +
    'min-height:30px;border:1px solid rgba(242,193,78,.7);border-radius:999px;background:rgba(10,16,34,.88);font:700 12px ' + FONT + ';color:#fff;z-index:20;pointer-events:none;white-space:nowrap;';
  const text = document.createElement('span');
  text.style.cssText = 'overflow:hidden;text-overflow:ellipsis;';
  const apply = document.createElement('button');
  apply.type = 'button'; apply.textContent = '지금 적용'; apply.dataset.testid = 'pwa-band-apply';
  apply.style.cssText = 'pointer-events:auto;cursor:pointer;min-height:44px;min-width:44px;margin:-8px 0;padding:0 12px;border-radius:999px;border:1.5px solid ' + C.gold + ';background:linear-gradient(#26335a,#1A2238);color:' + C.gold + ';font:800 12px ' + FONT + ';';
  const close = document.createElement('button');
  close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', '닫기'); close.dataset.testid = 'pwa-band-close';
  close.style.cssText = 'pointer-events:auto;cursor:pointer;min-height:44px;min-width:44px;margin:-8px -2px -8px 0;border:0;background:transparent;color:#cdd6f4;font:700 18px ' + FONT + ';';
  band.append(text, apply, close);
  root.appendChild(band);
  let dismissed = false;
  let shown = false, kBytes = -1;
  apply.addEventListener('click', () => { applyUpdateNow(); });
  close.addEventListener('click', () => { dismissed = true; update(); });
  let kText = '', dataAt = -1e9, dataCache = '';
  // 매 프레임 불린다: 데이터 진행(묶음 합계)은 0.5초마다만 다시 센다
  const dataNow = (): string => {
    const t = performance.now();
    if (t - dataAt > 500) { dataAt = t; dataCache = dataText(allDataStatus()); }
    return dataCache;
  };
  const update = (): void => {
    const s = pwaState();
    const vis = opts.visible();
    const upd = s.updateReady && !dismissed && bigUpdate(s.updateBytes) && vis;
    const data = !upd && vis ? dataNow() : '';
    const want = upd || data !== '';
    if (want !== shown) { shown = want; band.style.display = want ? 'flex' : 'none'; }
    apply.style.display = close.style.display = upd ? '' : 'none';
    band.style.padding = upd ? '2px 4px 2px 12px' : '4px 14px';
    const t = upd ? bandText(s.updateBytes) : data;
    if (want && (t !== kText || s.updateBytes !== kBytes)) { kBytes = s.updateBytes; kText = t; text.textContent = t; }
  };
  onPwaChange(update);
  return { update };
}
